import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  UserRound,
  Pill,
  ClipboardCheck,
  Info,
  Mail,
  Lock,
  ScrollText,
} from "lucide-react";
import PatientAppBar from "../../components/PatientAppBar/PatientAppBar";
import API from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { tokenManager } from "../../Api/tokenManager";
import { resetSocketConnection } from "../../Api/socket";
import { logout } from "../../Features/Postes/Slice/authSlice";
import { showConfirm } from "../../feedback/feedbackApi";
import "./PatientMenuPage.scss";

const ICON_STROKE = 1.75;

function formatNotificationTime(createdAt) {
  if (!createdAt) return "";
  try {
    const d = typeof createdAt === "string" ? new Date(createdAt) : createdAt;
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

/** Passed to menu tiles so sub-pages can show "← Menu" in the app bar. */
const PATIENT_MENU_LINK_STATE = {
  patientBack: { to: "/patient-menu", label: "Menu" },
};

const menuLinks = [
  {
    to: "/patient-profile",
    label: "Menu",
    description: "Profile & settings",
    Icon: UserRound,
  },
  {
    to: "/request/new",
    label: "Category & procedure",
    description: "Start new care request",
    Icon: Pill,
  },
  {
    to: "/pending-approval",
    label: "Approval status",
    description: "Account review",
    Icon: ClipboardCheck,
  },
  {
    to: "/AboutUs",
    label: "About us",
    description: "HealBid & mission",
    Icon: Info,
  },
  {
    to: "/contact",
    label: "Contact",
    description: "Reach the team",
    Icon: Mail,
  },
  {
    to: "/ConsentPage",
    label: "Consent & privacy",
    description: "Legal & preferences",
    Icon: Lock,
  },
];

export default function PatientMenuPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduxToken = useSelector((s) => s.auth?.token);

  const [logLoading, setLogLoading] = useState(true);
  const [activity, setActivity] = useState([]);

  const loadActivity = useCallback(async () => {
    const token =
      tokenManager.getTokenForRole("patient") || reduxToken || tokenManager.getToken();
    if (!token) {
      setActivity([]);
      setLogLoading(false);
      return;
    }
    setLogLoading(true);
    try {
      const res = await API.get(ENDPOINTS.PATIENT_NOTIFICATIONS, {
        params: { page: 1, limit: 6 },
      });
      const data = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(data) ? data : [];
      setActivity(list.slice(0, 6));
    } catch {
      setActivity([]);
    } finally {
      setLogLoading(false);
    }
  }, [reduxToken]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const handleLogout = async () => {
    const confirmed = await showConfirm("Are you sure you want to log out?", {
      title: "Log out?",
      confirmText: "Log out",
      cancelText: "Stay",
      danger: true,
    });
    if (!confirmed) return;
    dispatch(logout());
    resetSocketConnection();
    navigate("/", { replace: true });
  };

  return (
    <div className="patient-menu-page">
      <PatientAppBar title="Shortcuts" subtitle="Quick access to your care journey" />

      <div className="patient-menu-page__inner">
        <div className="patient-menu-page__home-shortcut">
          <Link
            to="/HealBidDashboard"
            className="patient-menu-page__home-shortcut-link"
          >
            ← Home
          </Link>
        </div>
        <section className="patient-menu-page__section" aria-labelledby="activity-heading">
          <div className="patient-menu-page__section-head">
            <h2 id="activity-heading" className="patient-menu-page__section-title">
              <ScrollText className="patient-menu-page__section-icon" strokeWidth={ICON_STROKE} aria-hidden />
              Recent activity
            </h2>
            <button
              type="button"
              className="patient-menu-page__refresh"
              onClick={loadActivity}
              disabled={logLoading}
              aria-label="Refresh activity"
            >
              Refresh
            </button>
          </div>
          <div className="patient-menu-page__activity-card">
            {logLoading ? (
              <ul className="patient-menu-page__activity-list" aria-busy="true">
                {[1, 2, 3].map((k) => (
                  <li key={k} className="patient-menu-page__activity-skel" />
                ))}
              </ul>
            ) : activity.length === 0 ? (
              <p className="patient-menu-page__activity-empty">
                No alerts yet. Updates about bids, appointments, and messages will appear here.
              </p>
            ) : (
              <ul className="patient-menu-page__activity-list">
                {activity.map((n, i) => (
                  <li key={`${n.type}-${n.messageId || n.bidLogId || i}`} className="patient-menu-page__activity-row">
                    <div className="patient-menu-page__activity-main">
                      <span className="patient-menu-page__activity-title">
                        {n.title || n.type || "Update"}
                      </span>
                      {n.body ? (
                        <span className="patient-menu-page__activity-body">{n.body}</span>
                      ) : null}
                    </div>
                    {(n.updatedAt || n.createdAt) && (
                      <time className="patient-menu-page__activity-time" dateTime={n.updatedAt || n.createdAt}>
                        {formatNotificationTime(n.updatedAt || n.createdAt)}
                      </time>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="patient-menu-page__section" aria-labelledby="shortcuts-heading">
          <h2 id="shortcuts-heading" className="patient-menu-page__section-title patient-menu-page__section-title--plain">
            Shortcuts
          </h2>
          <nav className="patient-menu-page__grid" aria-label="Care shortcuts">
            {menuLinks.map(({ to, label, description, Icon }) => (
              <Link
                key={to}
                className="patient-menu-page__tile"
                to={to}
                state={PATIENT_MENU_LINK_STATE}
              >
                <span className="patient-menu-page__tile-icon-wrap" aria-hidden>
                  <Icon className="patient-menu-page__tile-icon" strokeWidth={ICON_STROKE} />
                </span>
                <span className="patient-menu-page__tile-text">
                  <span className="patient-menu-page__label">{label}</span>
                  <span className="patient-menu-page__hint">{description}</span>
                </span>
              </Link>
            ))}
          </nav>
        </section>

        <button type="button" className="patient-menu-page__logout" onClick={handleLogout}>
          Log out
        </button>
      </div>
    </div>
  );
}
